import { useNavigate, useLocation, Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { LogOut, Wallet, User, LayoutDashboard, TrendingUp, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppHeader({ user, currency, onCurrencyChange, onUserUpdate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [savingPref, setSavingPref] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // proceed to login regardless
    }
    navigate('/login');
  };

  const handleToggleStatement = async (next) => {
    setSavingPref(true);
    try {
      const res = await fetch(`${API}/auth/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ monthly_statement_enabled: next }),
      });
      if (res.ok) {
        const updated = await res.json();
        if (onUserUpdate) onUserUpdate(updated);
        toast.success(next ? 'Monthly statements ON' : 'Monthly statements OFF');
      } else {
        toast.error('Failed to update preference');
      }
    } catch {
      toast.error('Failed to update preference');
    } finally {
      setSavingPref(false);
    }
  };

  const handleSendPreview = async () => {
    setSendingPreview(true);
    try {
      const res = await fetch(`${API}/statements/preview?currency=${currency}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'sent') {
          toast.success(`Preview sent to ${data.to}`);
        } else if (data.status === 'skipped') {
          toast.error(`Skipped: ${data.reason}`);
        } else {
          toast.error('Preview failed');
        }
      } else {
        const text = await res.text();
        toast.error(text || 'Preview send failed');
      }
    } catch {
      toast.error('Preview send failed');
    } finally {
      setSendingPreview(false);
    }
  };

  const navLinks = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
    { path: '/investments', label: 'Investments', icon: TrendingUp, testid: 'nav-investments' },
  ];

  return (
    <header className="border-b border-border bg-card sticky top-0 z-30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-6 min-w-0">
          <div className="flex items-center gap-2 shrink-0">
            <Wallet className="h-5 w-5 sm:h-6 sm:w-6" />
            <h1 className="text-base sm:text-xl tracking-tight font-bold hidden sm:block">Finance Tracker</h1>
          </div>
          <nav className="flex items-center gap-1 sm:gap-2">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={link.testid}
                  className={`px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 ${
                    active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                  }`}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{link.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <Select value={currency} onValueChange={onCurrencyChange}>
            <SelectTrigger data-testid="currency-selector" className="w-20 sm:w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="INR">INR</SelectItem>
              <SelectItem value="AED">AED</SelectItem>
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger data-testid="user-menu-trigger" asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="px-2 py-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <Mail className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium leading-tight">Monthly Statement</div>
                      <div className="text-xs text-muted-foreground leading-tight mt-0.5">1st of each month, PDF</div>
                    </div>
                  </div>
                  <Switch
                    data-testid="monthly-statement-toggle"
                    checked={!!user?.monthly_statement_enabled}
                    onCheckedChange={handleToggleStatement}
                    disabled={savingPref}
                  />
                </div>
                <button
                  data-testid="send-preview-statement-btn"
                  onClick={handleSendPreview}
                  disabled={sendingPreview}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium rounded-md border border-border px-3 py-2 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {sendingPreview ? 'Sending…' : 'Send preview now'}
                </button>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
